import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventOption } from '@frontend/sports/betting-offer/feature/model';
import { RacingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { HorseRaceSortingDirection, WinGameFixture } from '@frontend/sports/racing/feature/models';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { firstValueFrom } from 'rxjs';

import { GreyhoundRacingApiService } from '../../../../horse-racing-shared/services/greyhound-racing-api.service';
import { HorseRacingApiService } from '../../../../horse-racing-shared/services/horse-racing-api.service';
import { HorsesFixtureParticipantHelper } from '../../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { AdaptiveLayoutService } from '../../../../layout/adaptive-layout.service';
import { EachWayMarketHelperService } from '../../../../option-group/each-way-market-helper.service';
import { FixtureUpdateService } from '../../../common/fixture-update.service';
import { GreyhoundTrackingService } from '../../../common/greyhound-tracking.service';
import { RacingService } from '../../../services/racing-service';
import { NextRacesBaseComponent } from '../../next-races/next-races-base.component';
import { greyhoundnextRacesCardAnimation } from './animation';

@Component({
    selector: 'ms-gh-next-races',
    templateUrl: './gh-next-races.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [greyhoundnextRacesCardAnimation],
    standalone: false,
})
export class GhNextRacesComponent extends NextRacesBaseComponent {
    readonly displayRunners = true;
    sortBy = HorseRaceSortingDirection.CardNumber;
    readonly trackingSource = 'Greyhounds_NextRaces';
    participantOptionGroups: { [id: number]: EventOption[] } = {};
    groupTypes = OptionGroupType;

    constructor(
        public sitecore: Sitecore,
        protected override changeDetector: ChangeDetectorRef,
        private apiService: GreyhoundRacingApiService,
        protected urlHelper: UrlHelperService,
        protected greyhoundTrackingService: GreyhoundTrackingService,
        protected override racingConfig: RacingConfig,
        protected override horseRacingService: HorseRacingApiService,
        protected override greyhoundRacingService: GreyhoundRacingApiService,
        protected override racingService: RacingService,
        protected override layoutService: AdaptiveLayoutService,
        protected override eachWayMarketHelper: EachWayMarketHelperService,
        protected override horsesFixturePricingHelper: HorsesFixturePricingHelper,
        private horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        protected override fixtureUpdateService: FixtureUpdateService,
    ) {
        super(
            changeDetector,
            horseRacingService,
            greyhoundRacingService,
            racingConfig,
            racingService,
            layoutService,
            horsesFixturePricingHelper,
            eachWayMarketHelper,
            fixtureUpdateService,
        );
    }

    protected async refreshData(): Promise<void> {
        const fixtures = await firstValueFrom(this.apiService.getNextFixtures());
        this.fixtures.clear();
        this.subscribeToFixtures(fixtures);
        this.updateMarketTitles(fixtures, true);
        this.changeDetector.markForCheck();
    }

    track(race: WinGameFixture, i: number): void {
        const trackPosition = {} as TrackingSource;
        trackPosition.position = i;
        const fixtureUrl = this.urlHelper.getRacingUrlByFixture(race.sport.id, race);
        this.greyhoundTrackingService.trackViewFullCard(race, trackPosition, fixtureUrl);
    }

    countDownTime(race: WinGameFixture): number {
        return DateProviderService.getTimeDifference(new Date(race.startDate), true);
    }

    getFixtureLink(fixture: WinGameFixture): string {
        return this.urlHelper.getRacingUrlByFixture(fixture.sport.id, fixture);
    }

    getOptionGroup(fixture: WinGameFixture) {
        return fixture.optionGroups[0];
    }

    getDisplayType(fixture: WinGameFixture): string {
        return fixture.optionGroups[0].detailedGrouping?.displayType;
    }

    getParticipantOptionGroups(fixture: WinGameFixture) {
        const { participants } = fixture;
        if (this.getDisplayType(fixture) === OptionGroupType.Outrights) return [];

        return (this.participantOptionGroups = this.horsesFixtureParticipantHelper.getParticipantOptionGroups(
            this.getOptionGroup(fixture),
            this.horsesFixtureParticipantHelper.getParticipants(participants),
        ));
    }

    greyhoundTrapAndDogMapper(fixture: WinGameFixture) {
        return this.racingService.getGrehoundTrapDogMapping(this.horsesFixtureParticipantHelper.getParticipants(fixture.participants));
    }
}
